import { Link } from "react-router-dom";
import { ChevronRight, CalendarDays, ArrowRight } from "lucide-react";
import { Seo, SITE_URL, SITE_NAME } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { postPath } from "../data/posts";
import { serviceBySlug } from "../data/services";
import { servicePath } from "../data/cities";

export default function BlogPost({ post }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta,
    datePublished: post.date,
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: { "@type": "Organization", name: SITE_NAME },
    mainEntityOfPage: `${SITE_URL}${postPath(post)}`,
  };

  return (
    <div data-testid={`blog-post-${post.slug}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo title={`${post.title} | 1st Nations Security`} description={post.meta} path={postPath(post)} jsonLd={jsonLd} />
      <nav data-testid="blog-breadcrumb" className="text-xs text-slate-500 mb-6 flex items-center gap-1.5">
        <Link to="/" className="hover:text-amber-300">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/security-blog" className="hover:text-amber-300">Security Blog</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-300">{post.category}</span>
      </nav>

      <article className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3 flex items-center gap-3">
          {post.category}
          <span className="inline-flex items-center gap-1.5 text-slate-500 normal-case tracking-normal font-normal">
            <CalendarDays className="h-3.5 w-3.5" /> {post.date}
          </span>
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white font-heading mb-6">
          {post.title}
        </h1>
        <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-8">{post.excerpt}</p>
        {post.sections.map((s, i) => (
          <section key={i} className="mb-8" data-testid={`blog-section-${i}`}>
            {s.h && <h2 className="text-xl sm:text-2xl font-semibold text-amber-100 font-heading mb-3">{s.h}</h2>}
            {s.ps.map((p, j) => (
              <p key={j} className="text-base text-slate-300 leading-relaxed mb-4">{p}</p>
            ))}
          </section>
        ))}
      </article>

      <section data-testid="blog-related-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-5">Related Security Services</h2>
        <div className="flex flex-wrap gap-3">
          {post.related.map((slug) => {
            const s = serviceBySlug(slug);
            if (!s) return null;
            return (
              <Link
                key={slug}
                to={servicePath(s)}
                data-testid={`blog-related-link-${slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-amber-500/40 hover:text-amber-300"
              >
                {s.name} <ArrowRight className="h-3.5 w-3.5 text-amber-500" />
              </Link>
            );
          })}
        </div>
      </section>

      <CtaBand
        heading="Need Professional Security in Ontario?"
        sub="Call our 24/7 dispatch at +1 807-770-0243 or email admin@1stnation-security.com — licensed guards, mobile patrol, fire watch and more, anywhere in Ontario."
      />
    </div>
  );
}
